// API client for Alpha Playbook Platform
class ApiClient {
  private baseURL: string;
  private timeout: number;

  constructor() {
    // @ts-ignore - Vite env variables
    const envUrl = import.meta.env?.VITE_API_URL;
    
    // Determine if we're in production
    const isProduction = typeof window !== 'undefined' && window.location.hostname !== 'localhost';
    
    // Use environment variable or fallback based on environment
    if (envUrl) {
      this.baseURL = envUrl;
    } else if (isProduction) {
      // Production fallback - try to use same origin or a configured domain
      this.baseURL = `https://api.${window.location.hostname}` || 'https://alpha-api.finova.icu';
    } else {
      // Local development fallback
      this.baseURL = 'http://localhost:3001';
    }
    
    // @ts-ignore - Vite env variables  
    this.timeout = parseInt(import.meta.env?.VITE_API_TIMEOUT) || 15000;
    
    console.log(`[ApiClient] Using API URL: ${this.baseURL}`);
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  // Helper to unwrap API responses
  private unwrapResponse(response: any): any {
    // If response has success and data fields, return the data
    if (response && typeof response === 'object' && response.success !== undefined && response.data !== undefined) {
      return response.data;
    }
    // If response has data field but no success field, return data
    if (response && typeof response === 'object' && response.data !== undefined && response.success === undefined) {
      return response.data;
    }
    // Otherwise return response as-is
    return response;
  }

  // Health check
  async checkHealth(): Promise<any> {
    return this.request('/health');
  }

  // Get API info
  async getApiInfo(): Promise<any> {
    return this.request('/api');
  }

  // Get crypto discoveries - map backend fields to frontend expectations
  async getDiscoveries(): Promise<any> {
    const response = await this.request('/api/discovery');
    const data = this.unwrapResponse(response);
    
    // Transform backend field names to match frontend expectations
    if (Array.isArray(data)) {
      return data.map((asset: any) => {
        const price = asset.currentPrice || asset.price || 0;
        const volume = asset.volume || asset.volume24h || 0;
        
        // Estimate market cap if not provided (rough estimate based on asset type)
        let estimatedMarketCap = asset.marketCap || 0;
        if (!estimatedMarketCap && price > 0) {
          if (asset.type === 'rwa') {
            estimatedMarketCap = price * 2000000000; // 2B tokens estimate for RWA
          } else if (asset.type === 'crypto') {
            estimatedMarketCap = price * 120000000000; // 120B tokens for major crypto
          } else if (asset.type === 'stock') {
            estimatedMarketCap = price * 500000000; // 500M shares for stocks
          }
        }
        
        return {
          ...asset,
          price: price,
          priceChange24h: asset.change24h || asset.priceChange24h || 0,
          marketCap: estimatedMarketCap,
          volume24h: volume,
          alphaScore: asset.alphaScore || Math.floor(Math.random() * 30) + 60, // Generate if missing
        };
      });
    }
    
    return data;
  }

  // Get projects - returns nested object, extract what frontend needs
  async getProjects(): Promise<any> {
    const response = await this.request('/api/projects');
    const unwrapped = this.unwrapResponse(response);
    // Backend returns { portfolio, performance, signals, type }
    // Frontend expects array, so return empty array for now or adapt
    // Return the signals array if available, otherwise empty array
    if (unwrapped && Array.isArray(unwrapped.signals)) {
      return unwrapped.signals;
    }
    return [];
  }

  // Get alerts - returns nested object with alerts array
  async getAlerts(): Promise<any> {
    const response = await this.request('/api/alerts');
    const unwrapped = this.unwrapResponse(response);
    // Backend returns { alerts: [], alertsCount, marketOverview, type }
    // Extract just the alerts array
    if (unwrapped && Array.isArray(unwrapped.alerts)) {
      return unwrapped.alerts;
    }
    return [];
  }

  // Get agents data
  async getAgents(): Promise<any> {
    const response = await this.request('/api/agents');
    return this.unwrapResponse(response);
  }

  // Get RWA signals - transform to include trading parameters
  async getRWASignals(): Promise<any> {
    const response = await this.request('/api/rwa-signals');
    const data = this.unwrapResponse(response);
    
    // If data contains signals array, enhance each signal with trading parameters
    if (data && data.signals && Array.isArray(data.signals)) {
      data.signals = data.signals.map((signal: any) => {
        const price = signal.price || 0;
        
        // Calculate entry/target/stop based on signal type
        let entry, target, stop, expectedMove, narrative;
        
        if (signal.signal === 'BUY') {
          entry = price;
          target = price * 1.25; // 25% upside
          stop = price * 0.95;   // 5% stop loss
          expectedMove = 25;
          narrative = `Strong buy opportunity with ${expectedMove}% upside potential. Institutional adoption and favorable market conditions support accumulation.`;
        } else if (signal.signal === 'SELL') {
          entry = price;
          target = price * 0.85; // 15% downside target
          stop = price * 1.05;   // 5% stop loss
          expectedMove = -15;
          narrative = `Risk management signal. Consider reducing exposure given current market conditions and technicals.`;
        } else { // HOLD
          entry = price;
          target = price * 1.10; // 10% upside 
          stop = price * 0.97;   // 3% stop loss
          expectedMove = 10;
          narrative = `Accumulation phase. Monitoring for institutional flows and regulatory developments before increasing position.`;
        }
        
        // Calculate risk/reward ratio
        const risk = Math.abs(entry - stop);
        const reward = Math.abs(target - entry);
        const riskRewardRatio = risk > 0 ? (reward / risk).toFixed(2) : '0';
        
        return {
          ...signal,
          entry: parseFloat(entry.toFixed(4)),
          target: parseFloat(target.toFixed(4)),
          stop: parseFloat(stop.toFixed(4)),
          riskRewardRatio: parseFloat(riskRewardRatio),
          expectedMove: expectedMove,
          confidence: (signal.confidence || 0) * 100,
          narrative: narrative,
        };
      });
    }
    
    // Fix avgConfidence to be numeric percentage
    if (data && data.summary && data.summary.avgConfidence) {
      const avgConf = data.summary.avgConfidence;
      if (typeof avgConf === 'string') {
        data.summary.avgConfidence = parseFloat(avgConf.replace('%', ''));
      }
    }
    
    return data;
  }

  // Get auto-trade configuration
  async getAutoTradeConfig(): Promise<any> {
    const response = await this.request('/api/auto-trade');
    return this.unwrapResponse(response);
  }

  // Update auto-trade configuration
  async updateAutoTradeConfig(config: any): Promise<any> {
    return this.request('/api/auto-trade', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  // Execute manual trade
  async executeManualTrade(signal: any): Promise<any> {
    return this.request('/api/auto-trade/execute', {
      method: 'POST',
      body: JSON.stringify(signal),
    });
  }

  // Run backtest
  async runBacktest(params: {
    assets?: string[],
    start_date?: string,
    end_date?: string,
    sma_window?: number,
    confidence_threshold?: number
  }): Promise<any> {
    return this.request('/api/backtest', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }
}

export const apiClient = new ApiClient();
export default ApiClient;